import { z } from 'zod'

import { ProblemSchema } from '~/@entities/Problem'
import {
  AllGradeSubjectSchema,
  GradeSchema,
  HighSchoolSubjectSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'

// 서버는 Asia/Seoul LocalDateTime 을 타임존 없이 낸다.
const _LocalDateTimeSchema = z.iso.datetime({ local: true })

/***************** 시험 종류 *****************/
export const EXAM_TYPE = [
  'MIDDLE_SCHOOL_ACHIEVEMENT_EVALUATION',
  'MAAT',
  'KMM',
  'KMM_PRACTICE_TEST',
  'ACADEMY_GROUP',
] as const

/***************** 시험 일정 진행 상태 *****************/
export const EXAM_SCHEDULE_PROGRESS = [
  'READY',
  'PRE_OPEN',
  'IN_PROGRESS',
  'GRADING',
  'RESOLVED',
] as const

export const ExamTypeSchema = z.enum(EXAM_TYPE)
export type ExamType = z.infer<typeof ExamTypeSchema>

export const ExamScheduleProgressSchema = z.enum(EXAM_SCHEDULE_PROGRESS)
export type ExamScheduleProgress = z.infer<typeof ExamScheduleProgressSchema>

// 시험 본체는 아직 legacy 라 학생-시험 목록(StudentExamWithExamResponse)이 품고 오는 ExamResponse 중
// 앱이 읽는 것만 받는다. legacy 진행 상태 `progress` 는 scheduleProgress 로 대체되어 받지 않는다.
export const ExamSchema = z.object({
  id: z.number(),
  examType: ExamTypeSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: z.number().nullable(),
  scheduleProgress: ExamScheduleProgressSchema,
  title: z.string(),
  chapter: z.string(),
  deleted: z.boolean(),
  openEndDate: z.iso.date(),
  problemCount: z.number(),
})
export type Exam = z.infer<typeof ExamSchema>

/** 시험지에 실린 문항 하나 — 시험 안의 위치·배점과 문제 본체 */
export const ExamProblemSchema = z.object({
  id: z.number(),
  examId: ExamSchema.shape.id,
  problemId: ProblemSchema.shape.id,
  orderingNumber: z.number(),
  score: z.number(),
  problem: ProblemSchema,
})
export type ExamProblem = z.infer<typeof ExamProblemSchema>

// 학원 설정(academyAdditionalData)이 싣는 학교급·학년별 시험 자동 배정 여부. 시험(KMM)이 초·중만 있어 고등은 오지 않는다.
// 학년은 숫자로 온다(서버 ExamAutoAssigning.grade: Int).
export const ExamAutoAssigningSchema = z.object({
  schoolType: SchoolTypeSchema.exclude(['HIGH']),
  grade: GradeSchema,
  useAutoAssigning: z.boolean(),
})
export type ExamAutoAssigning = z.infer<typeof ExamAutoAssigningSchema>

/***************** 시험 회차 일정 *****************/
// 보고서 생성 시작 시각(reportCreationStartDatetime)도 오지만 앱이 기준으로 삼으면 안 되는 값이라 받지 않는다.
export const ExamScheduleProgressTimelineSchema = z.object({
  examPreOpenDatetime: _LocalDateTimeSchema,
  examStartDatetime: _LocalDateTimeSchema,
  examEndDatetime: _LocalDateTimeSchema,
  gradingStartDatetime: _LocalDateTimeSchema,
  gradingEndDatetime: _LocalDateTimeSchema,
  // 고등 모의고사 회차는 수상이 없어 null 이다
  awardRequestStartDatetime: _LocalDateTimeSchema.nullable(),
  awardRequestEndDatetime: _LocalDateTimeSchema.nullable(),
  examPdfDownloadStartDatetime: _LocalDateTimeSchema.nullable(),
  examPdfDownloadEndDatetime: _LocalDateTimeSchema.nullable(),
  examScheduleChanged: z.boolean(),
})
export type ExamScheduleProgressTimeline = z.infer<typeof ExamScheduleProgressTimelineSchema>

/** 회차에 열리는 시험 한 종(학교급·학년 단위) */
export const ExamScheduleExamSchema = z.object({
  type: ExamTypeSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  subject: HighSchoolSubjectSchema.nullable(),
  chapter: z.string().nullable(),
})
export type ExamScheduleExam = z.infer<typeof ExamScheduleExamSchema>

/** 한 달에 한 번 열리는 시험 회차 */
export const ExamScheduleSchema = z.object({
  year: z.number(),
  month: z.number(),
  round: z.number(),
  title: z.string(),
  progress: ExamScheduleProgressSchema,
  progressTimeline: ExamScheduleProgressTimelineSchema,
  exams: z.array(ExamScheduleExamSchema),
})
export type ExamSchedule = z.infer<typeof ExamScheduleSchema>

/** 내려받을 시험 결과지 파일 */
export const ExamReportFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type ExamReportFile = z.infer<typeof ExamReportFileSchema>
